import { useId, useRef, useState } from 'react';
import type { TeammateTopic } from '@mg/shared';
import { Plus } from 'lucide-react';
import { MemberAvatar } from '../../components/ui/MemberAvatar';
import { cn } from '../../lib/cn';
import { remPx } from '../../lib/rem';
import { useDismissable } from '../../lib/useDismissable';
import { stackedTeammates, usedByLabel } from './teammateTopics';

/**
 * The popover is this wide (w-56). It is kept this far inside the viewport on both sides. In rem, like
 * the popover: the sums below are in px, at the scale on screen (224 and 16 at full size).
 */
const POPOVER_WIDTH_REM = 14;
const VIEWPORT_GUTTER_REM = 1;

/**
 * One "Used by teammates" suggestion: [+ label] adds a copy of the topic to the user's list, and
 * the avatars-and-count half is a separate button that opens a USED BY popover listing everyone
 * who tracks it. The popover closes on a click outside it or Esc; the panel keeps at most one open.
 */
export function TeammateTopicChip({
  topic,
  adding,
  open,
  addButtonId,
  onAdd,
  onToggle,
  onDismiss,
}: {
  topic: TeammateTopic;
  adding: boolean;
  open: boolean;
  /** So the panel can put focus back here when the user undoes the add. */
  addButtonId: string;
  onAdd: () => void;
  onToggle: () => void;
  /** Closes the popover on an outside click or Esc. Keep it stable (useCallback). */
  onDismiss: () => void;
}) {
  const popoverId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  // Where the popover starts, relative to the avatar button's left edge.
  const [offsetLeft, setOffsetLeft] = useState(0);

  // The ref spans the trigger AND the popover, so clicking either is not an "outside" click.
  useDismissable(open, rootRef, onDismiss);

  // Line the popover up with the button, sliding it left only as far as it takes to stay on screen.
  // Neither edge alone works on a phone: a chip mid-row has too little room on either side.
  const toggle = () => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) {
      const rem = remPx();
      const gutter = VIEWPORT_GUTTER_REM * rem;
      const roomRight = window.innerWidth - gutter - POPOVER_WIDTH_REM * rem - rect.left;
      setOffsetLeft(Math.max(gutter - rect.left, Math.min(0, roomRight)));
    }
    onToggle();
  };

  return (
    <div className="inline-flex items-stretch rounded-full border border-line-input bg-card text-xs font-medium">
      <button
        id={addButtonId}
        type="button"
        // The visible "+ label" does not say what the button does once the "+" icon is hidden from
        // assistive tech, so the name spells it out — and still contains the label it shows.
        aria-label={`Add ${topic.label} to my topics`}
        // aria-disabled, not disabled: a disabled button drops keyboard focus on the spot, and this
        // one is about to hand its focus to the topic it adds.
        aria-disabled={adding || undefined}
        onClick={() => {
          if (!adding) onAdd();
        }}
        className={cn(
          'focus-ring inline-flex items-center gap-1 rounded-l-full py-1 pl-2.5 pr-2 text-link transition-colors hover:bg-tint',
          adding && 'cursor-progress opacity-50',
        )}
      >
        <Plus className="h-3 w-3" aria-hidden="true" />
        {topic.label}
      </button>
      <span aria-hidden="true" className="w-px bg-line-input" />
      <div ref={rootRef} className="relative flex">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          aria-label={usedByLabel(topic.users)}
          onClick={toggle}
          className="focus-ring inline-flex items-center gap-1.5 rounded-r-full py-0.5 pl-1.5 pr-2.5 text-ink-label transition-colors hover:bg-hover"
        >
          <span className="flex -space-x-1.5" aria-hidden="true">
            {stackedTeammates(topic.users).map((user) => (
              <MemberAvatar key={user.id} person={user} size="xs" className="ring-2 ring-card" />
            ))}
          </span>
          <span className="tabular-nums">{topic.users.length}</span>
        </button>

        {open && (
          <div
            id={popoverId}
            role="group"
            aria-label="Used by"
            style={{ left: offsetLeft }}
            className="absolute top-full z-20 mt-1.5 w-56 rounded-lg border border-line bg-card p-2 shadow-panel"
          >
            <p className="px-1 pb-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-muted">
              Used by
            </p>
            <ul className="max-h-64 space-y-1 overflow-y-auto">
              {topic.users.map((user) => (
                <li key={user.id} className="flex items-center gap-2 px-1 py-1">
                  {/* The name is printed beside it; announcing the picture too would say it twice. */}
                  <MemberAvatar person={user} size="sm" decorative />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">{user.name}</p>
                    <p className="truncate text-xs text-ink-muted">{user.role}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
